// src/components/CustomRating/CustomRating.tsx
import React from 'react';
import styled from '@emotion/styled';
import clsx from 'clsx';
import { useField, useFormikContext } from 'formik';

interface CustomRatingProps {
  /** The Formik field name */
  name: string;
}

const CustomRating: React.FC<CustomRatingProps> = ({ name }) => {
  // connect to Formik
  const [field, meta, helpers] = useField<number>(name);
  const { setValue } = helpers;
  const { setFieldTouched } = useFormikContext<{ [key: string]: any }>();

  const handleClick = (rating: number) => {
    // 1) update the value (this triggers validation)
    setValue(rating);
    // 2) mark touched *without* re-validating on touch
    setFieldTouched(name, true, false);
  };

  return (
    <Container>
      <Question>
        How likely are you to recommend Locumspace to others ?
      </Question>

      <RatingContainer>
        {Array.from({ length: 10 }, (_, i) => i + 1).map((num) => (
          <RatingCircle
            key={num}
            type="button"
            className={clsx({ active: field.value === num })}
            onClick={() => handleClick(num)}
          >
            {num}
          </RatingCircle>
        ))}
      </RatingContainer>

      <InfoText>
        1- least likely <span>10- most likely</span>
      </InfoText>

      {meta.touched && meta.error && <ErrorText>{meta.error}</ErrorText>}
    </Container>
  );
};

export default CustomRating;

/** Styled components **/

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const Question = styled.h4`
  margin: 0;
  font-weight: 400;
`;

const RatingContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;

  @media (max-width: 480px) {
    gap: 2px;
  }
`;

const RatingCircle = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid transparent;
  background-color: white;
  font-size: 0.8rem;
  cursor: pointer;
  transition: border-color 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;

  &:hover {
    border-color: ${({ theme }) => theme.palette.mainBlue};
  }

  &.active {
    border-color: ${({ theme }) => theme.palette.mainBlue};
  }

  @media (max-width: 480px) {
    width: 22px;
    height: 22px;
    font-size: 12px;
  }
`;

const InfoText = styled.p`
  margin: 0;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.palette.greyGrey2};

  & > span {
    margin-left: 1rem;
  }
`;

const ErrorText = styled.div`
  margin-top: 4px;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.palette.stateColorRed};
`;
